import React, { useCallback, useContext, useEffect, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft } from "react-feather";
import { Form, InputNumber, message, Spin } from "antd";
import type { FormInstance } from "antd";

import ToggleSwitch from "../../../../CommonComponents/ToggleSwitch/ToggleSwitch";
import { AppStrings } from "../../../../../utils/constants/AppStrings";
import {
  FRAUD_DETECTION_RULE_ID_QUERY_KEY,
  NAVIGATION_ROUTES,
} from "../../../../../utils/NavigationUtils";

import { type FraudDetectionRuleRow } from "../FraudDetectionRulesTable";
import { useUpdateFraudRule } from "../../../../../queries/fraud-detection/useUpdateFraudRule";

import styles from "./stylesEditRule.module.css";
import { SIDER_TABS } from "../../../../../utils/Constants";
import AppContext from "../../../../../context/AppContext";
import CustomFormInput from "../../../../CommonComponents/CustomFormInput/CustomFormInput";
import CustomSelect from "../../../../CommonComponents/CustomSelect/CustomSelect";
import { useCurrencySymbol } from "../../../../../hooks/useCurrencySymbol";
import { formatCurrency } from "../../../../../utils/financialFormatters";
import { usePageTitle } from "../../../../CommonComponents/Layout/PageTitleContext";

type EditRuleLocationState = {
  rule?: FraudDetectionRuleRow;
};

export type EditRuleWindowUnit = "seconds" | "minutes" | "hours" | "days";

export type EditRuleFormValues = {
  name: string;
  rule_active: boolean;
  deposit_threshold: number | null;
  evaluation_window_value: number | null;
  evaluation_window_unit: EditRuleWindowUnit;
};

const EVALUATION_WINDOW_LABEL = "Evaluation window";

const WINDOW_UNIT_SECONDS: Record<EditRuleWindowUnit, number> = {
  seconds: 1,
  minutes: 60,
  hours: 3600,
  days: 86400,
};

const EVALUATION_WINDOW_UNIT_OPTIONS: {
  value: EditRuleWindowUnit;
  label: string;
}[] = [
  { value: "seconds", label: "Seconds" },
  { value: "minutes", label: "Minutes" },
  { value: "hours", label: "Hours" },
  { value: "days", label: "Days" },
];

/** Picks the largest unit that cleanly divides the given seconds. */
function pickEvaluationWindowUnit(seconds: number | undefined | null): {
  value: number;
  unit: EditRuleWindowUnit;
} {
  if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) {
    return { value: 1, unit: "hours" };
  }
  if (seconds % 86400 === 0) return { value: seconds / 86400, unit: "days" };
  if (seconds % 3600 === 0) return { value: seconds / 3600, unit: "hours" };
  if (seconds % 60 === 0) return { value: seconds / 60, unit: "minutes" };
  return { value: seconds, unit: "seconds" };
}

/**
 * Guard logic for PATCH payload; exported for unit tests (Form rules already
 * block most invalid submits, so this stays as a runtime safety net).
 */
export function getEditRuleSaveValidationError(
  values: EditRuleFormValues,
): string | null {
  const deposit = values.deposit_threshold;
  const windowValue = values.evaluation_window_value;
  if (deposit == null || !Number.isFinite(deposit)) {
    return AppStrings.FIELD_IS_REQUIRED.replace("{title}", AppStrings.VALUE);
  }
  if (windowValue == null || !Number.isFinite(windowValue) || windowValue <= 0) {
    return AppStrings.FIELD_IS_REQUIRED.replace(
      "{title}",
      EVALUATION_WINDOW_LABEL,
    );
  }
  // Compare in seconds so fractional-unit values (e.g. 0.04375 days = 3,780 s)
  // still validate as long as the total window is at least one second.
  const seconds = computeEvaluationWindowSeconds(
    windowValue,
    values.evaluation_window_unit,
  );
  if (seconds < 1) {
    return AppStrings.FIELD_IS_REQUIRED.replace(
      "{title}",
      EVALUATION_WINDOW_LABEL,
    );
  }
  return null;
}

/**
 * Formats the evaluation window value for display:
 * - Integers render with thousand separators, no decimals (e.g. `3,600`).
 * - Decimals are shown with thousand separators on the integer part,
 *   preserving meaningful fractional digits (e.g. `0.04375`).
 *
 * Uses `toFixed(10)` instead of `String(num)` so very small numbers (e.g.
 * `1e-7`) don't fall back to scientific notation, which `split(".")` would
 * mishandle.
 */
export function evaluationWindowFormatter(
  value: string | number | undefined,
): string {
  if (value === undefined || value === null || value === "") return "";
  const num = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(num)) return String(value);
  if (Number.isInteger(num)) {
    return num.toLocaleString("en-US");
  }
  const [intPart, decPart] = num
    .toFixed(10)
    .replace(/0+$/, "")
    .split(".");
  const intFormatted = Number(intPart).toLocaleString("en-US");
  return decPart ? `${intFormatted}.${decPart}` : intFormatted;
}

/** Strips thousand separators so the raw numeric value is stored in the form. */
export function evaluationWindowParser(display: string | undefined): string {
  if (!display) return "";
  return display.replace(/,/g, "");
}

/** Converts the form's (value, unit) pair into seconds (>= 1). */
export function computeEvaluationWindowSeconds(
  value: number,
  unit: EditRuleWindowUnit,
): number {
  const seconds = Math.floor(value * (WINDOW_UNIT_SECONDS[unit] ?? 1));
  return Math.max(1, seconds);
}

type WindowUnitSelectProps = {
  /** Injected by Form.Item; current unit. */
  value?: EditRuleWindowUnit;
  /** Injected by Form.Item; updates the unit field. */
  onChange?: (unit: EditRuleWindowUnit) => void;
  /** Form instance so we can reach the sibling value field. */
  form: FormInstance<EditRuleFormValues>;
};

/**
 * Unit dropdown that also rewrites `evaluation_window_value` so the total
 * number of seconds stays constant when the user swaps units (e.g. `1 hour`
 * becomes `60` when switching to minutes). Only triggers on user-initiated
 * changes — programmatic `setFieldsValue` calls during rule hydration won't
 * invoke `onChange`, so existing rules hydrate without double conversion.
 */
const WindowUnitSelect: React.FC<WindowUnitSelectProps> = ({
  value,
  onChange,
  form,
}) => {
  const handleChange = (next: EditRuleWindowUnit) => {
    const previous = value;
    const currentValue = form.getFieldValue(
      "evaluation_window_value",
    ) as number | null | undefined;
    if (
      previous &&
      next !== previous &&
      currentValue != null &&
      Number.isFinite(currentValue)
    ) {
      // Round the total to integer seconds so the round-trip is stable (e.g.
      // 63 min → days → min comes back to 63, not 62.99999999…).
      const totalSeconds = Math.round(
        currentValue * WINDOW_UNIT_SECONDS[previous],
      );
      const converted = totalSeconds / WINDOW_UNIT_SECONDS[next];
      if (Number.isFinite(converted) && converted > 0) {
        // Trim residual FP noise to 6 decimals. Integers stay integers.
        const cleaned = Math.round(converted * 1e6) / 1e6;
        form.setFieldsValue({ evaluation_window_value: cleaned });
      }
    }
    onChange?.(next);
  };

  return (
    <CustomSelect<EditRuleWindowUnit>
      value={value}
      onChange={(v) => handleChange(v as EditRuleWindowUnit)}
      options={EVALUATION_WINDOW_UNIT_OPTIONS}
      style={{ width: 140 }}
      aria-label={`${EVALUATION_WINDOW_LABEL} unit`}
    />
  );
};

function ruleStatusToActive(status: string): boolean {
  return status.trim().toLowerCase() === "active";
}

function thresholdToNumber(raw: string | number | undefined): number | null {
  if (raw == null || raw === "") return null;
  const n = typeof raw === "number" ? raw : Number(String(raw).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : null;
}

type FormToggleSwitchProps = {
  value?: boolean;
  onChange?: (active: boolean) => void;
};

const FormToggleSwitch: React.FC<FormToggleSwitchProps> = ({ value, onChange }) => (
  <ToggleSwitch
    status={value ?? false}
    eventCode="fraud_rule_active"
    data={null}
    updateEventCode={(payload: { is_active: boolean }) => {
      onChange?.(payload.is_active);
    }}
  />
);

const currencyFormatter = (
  value: string | number | undefined,
  currencySymbol?: string,
): string => {
  if (value === undefined || value === "") return "";
  const n =
    typeof value === "number"
      ? value
      : Number(String(value).replace(/[^0-9.-]/g, ""));
  if (!Number.isFinite(n)) return "";
  return formatCurrency(n, currencySymbol, Number.isInteger(n) ? 0 : 2);
};

const currencyParser = (display: string | undefined): string => {
  return display?.replace(/[^0-9.-]/g, "") ?? "";
};

function buildInitialFormFromRule(rule: FraudDetectionRuleRow): EditRuleFormValues {
  const deposit =
    rule.deposit_threshold != null
      ? thresholdToNumber(rule.deposit_threshold)
      : thresholdToNumber(rule.conditions[0]?.threshold_value);
  const windowSec =
    rule.evaluation_window_seconds ??
    (() => {
      const p = rule.conditions[0]?.period ?? "";
      const m = /^(\d+)\s*hour/i.exec(p);
      if (m) return Number(m[1]) * 3600;
      const d = /^(\d+)\s*day/i.exec(p);
      if (d) return Number(d[1]) * 86400;
      return 3600;
    })();

  const { value: windowValue, unit: windowUnit } = pickEvaluationWindowUnit(
    Number.isFinite(windowSec) ? windowSec : 3600,
  );

  return {
    name: rule.name,
    rule_active: rule.active ?? ruleStatusToActive(rule.status),
    deposit_threshold: deposit,
    evaluation_window_value: windowValue,
    evaluation_window_unit: windowUnit,
  };
}

const EditRule: React.FC = () => {
  const { setActiveSidebarTab } = useContext(AppContext);
  const currencySymbol = useCurrencySymbol();

  const [form] = Form.useForm<EditRuleFormValues>();
  const ruleActiveWatched = Form.useWatch("rule_active", form);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const { mutate: updateRule, isPending: isSaving } = useUpdateFraudRule();

  const ruleId = searchParams.get(FRAUD_DETECTION_RULE_ID_QUERY_KEY);
  const ruleFromState = (location.state as EditRuleLocationState | null)?.rule;

  const resolvedRule = useMemo((): FraudDetectionRuleRow | undefined => {
    if (ruleFromState && (!ruleId || ruleFromState.id === ruleId)) {
      return ruleFromState;
    }
    return undefined;
  }, [ruleFromState, ruleId]);
  const pageTitle = resolvedRule?.name
    ? `${AppStrings.EDIT_RULE_TITLE}: ${resolvedRule.name}`
    : AppStrings.EDIT_RULE_TITLE;
  usePageTitle(pageTitle);

  useEffect(() => {
    if (resolvedRule) {
      form.setFieldsValue(buildInitialFormFromRule(resolvedRule));
    } else {
      form.resetFields();
    }
  }, [form, resolvedRule]);

  useEffect(() => {
    if (!resolvedRule) {
      message.error(AppStrings.PAM_FRAUD_RULE_NOT_FOUND);
      navigate(NAVIGATION_ROUTES.FRAUD_DETECTION);
    }
  }, [resolvedRule, navigate]);

  useEffect(() => {
    setActiveSidebarTab(SIDER_TABS.COMPLIANCE, "FRAUD_DETECTION");
  }, [setActiveSidebarTab, location.pathname]);

  const handleBack = () => {
    navigate(NAVIGATION_ROUTES.FRAUD_DETECTION);
  };

  const handleSave = useCallback(
    (values: EditRuleFormValues) => {
      if (!resolvedRule) return;

      const validationError = getEditRuleSaveValidationError(values);
      if (validationError) {
        message.error(validationError);
        return;
      }

      const deposit = values.deposit_threshold as number;
      const windowSec = computeEvaluationWindowSeconds(
        values.evaluation_window_value as number,
        values.evaluation_window_unit,
      );

      updateRule(
        {
          rule_name: values.name.trim(),
          deposit_threshold: deposit,
          evaluation_window_seconds: windowSec,
          active: values.rule_active,
        },
        {
          onSuccess: () => {
            message.success(AppStrings.VALUES_UPDATED_SUCCESSFULLY);
            navigate(NAVIGATION_ROUTES.FRAUD_DETECTION);
          },
          onError: () => {
            message.error(AppStrings.FAILED_TO_UPDATE_STATUS);
          },
        },
      );
    },
    [resolvedRule, updateRule, navigate],
  );

  return (
    <>
      <div className={styles.editRulePage}>
        <div
          className={styles.pageBody}
          aria-busy={isSaving}
        >
        <header className={styles.header}>
          <button
            type="button"
            className={styles.backButton}
            onClick={handleBack}
            aria-label={AppStrings.CANCEL}
            disabled={isSaving}
          >
            <ChevronLeft size={20} />
          </button>
        </header>

        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          className={styles.form}
          initialValues={{
            rule_active: true,
            deposit_threshold: null,
            evaluation_window_value: 1,
            evaluation_window_unit: "hours",
          }}
          onFinish={handleSave}
        >
          <section
            className={styles.topSection}
            aria-labelledby="edit-rule-details-heading"
          >
            <div className={styles.nameAndStatusRow}>
              <Form.Item
                className={`${styles.formField} ${styles.nameField}`}
                name="name"
                label={AppStrings.NAME}
                rules={[
                  {
                    required: true,
                    message: AppStrings.FIELD_IS_REQUIRED.replace(
                      "{title}",
                      AppStrings.NAME,
                    ),
                  },
                ]}
              >
                <CustomFormInput placeholder={AppStrings.NAME} />
              </Form.Item>
              <div
                className={`${styles.formField} ${styles.statusToggleField}`}
              >
                <div
                  className={styles.statusToggleControl}
                  aria-label={AppStrings.STATUS}
                >
                  <span className={styles.statusToggleCaption}>
                    {(ruleActiveWatched ?? true)
                      ? AppStrings.ACTIVE_FILTER_OPTION
                      : AppStrings.INACTIVE}
                  </span>
                  <Form.Item name="rule_active" noStyle>
                    <FormToggleSwitch />
                  </Form.Item>
                </div>
              </div>
            </div>
          </section>

          <div className={styles.divider} />

          <section
            className={styles.conditionsSection}
            aria-labelledby="edit-rule-conditions-heading"
          >
            <h2
              id="edit-rule-conditions-heading"
              className={styles.sectionTitle}
            >
              {AppStrings.CONDITIONS_TITLE}
            </h2>
            <div className={styles.conditionsFormContainer}>
            <Form.Item
              className={styles.formField}
              name="deposit_threshold"
              label={AppStrings.THRESHOLD_APPLIES_TO_DEPOSIT_WAGER_WITHDRAWAL}
              extra={AppStrings.THRESHOLD_GREATER_THAN_OR_EQUAL_HINT}
              rules={[
                {
                  required: true,
                  message: AppStrings.FIELD_IS_REQUIRED.replace(
                    "{title}",
                    AppStrings.VALUE,
                  ),
                },
              ]}
            >
              <InputNumber<string | number>
                className={styles.conditionValueInput}
                controls={false}
                min={0}
                placeholder={AppStrings.MIN_TRANSACTION_PLACEHOLDER}
                formatter={(value) =>
                  currencyFormatter(value, currencySymbol)
                }
                parser={currencyParser}
                style={{ width: "100%" }}
              />
            </Form.Item>
            <Form.Item
              className={styles.formField}
              label={EVALUATION_WINDOW_LABEL}
              required
            >
              <div className={styles.evaluationWindowRow}>
                <Form.Item name="evaluation_window_unit" noStyle>
                  <WindowUnitSelect form={form} />
                </Form.Item>
                <Form.Item
                  name="evaluation_window_value"
                  noStyle
                  rules={[
                    {
                      required: true,
                      message: AppStrings.FIELD_IS_REQUIRED.replace(
                        "{title}",
                        EVALUATION_WINDOW_LABEL,
                      ),
                    },
                  ]}
                >
                  <InputNumber
                    className={`${styles.conditionValueInput} ${styles.evaluationWindowInput}`}
                    controls={false}
                    min={0}
                    step={1}
                    placeholder="1"
                    formatter={evaluationWindowFormatter}
                    parser={evaluationWindowParser}
                  />
                </Form.Item>
              </div>
            </Form.Item>
            </div>
          </section>

          <div className={styles.divider} />

          <div className={styles.formActions}>
            <button type="submit" className={styles.saveButton} disabled={isSaving}>
              {isSaving ? AppStrings.SAVING : AppStrings.SAVE_CHANGES_BUTTON}
            </button>
          </div>
        </Form>

        {isSaving ? (
          <div
            className={styles.savingOverlay}
            role="status"
            aria-live="polite"
            aria-label={AppStrings.SAVING}
            data-testid="edit-rule-saving-overlay"
          >
            <div className={styles.savingSpinStack}>
              <Spin size="large" className={styles.savingSpin} />
              <span className={styles.savingLabel}>{AppStrings.SAVING}</span>
            </div>
          </div>
        ) : null}
        </div>
      </div>
    </>
  );
};

export default EditRule;
